import foreseeLogo from '../NovaWidget/assets/foresee-eagle.png';
import ModuleIcon from '../Components/ModuleIcon';
import './SignIn.css';

// ── Shared icons ──────────────────────────────────────────────────────
export function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="3"/>
      <path d="M3.5 7.5l8.5 6 8.5-6"/>
    </svg>
  );
}
export function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
      <path d="M8 10.5V8a4 4 0 018 0v2.5M12 15v2"/>
    </svg>
  );
}
export function EyeButton({ visible, onToggle }) {
  return (
    <button type="button" className="lg-eye"
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      onMouseDown={e => e.preventDefault()}
      onClick={onToggle}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/>
        <circle cx="12" cy="12" r="3"/>
        {!visible && <path d="M4 4l16 16"/>}
      </svg>
    </button>
  );
}
export function AlertCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 7.5v5.5M12 16.4v.1"/>
    </svg>
  );
}
export function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4 12 14.01l-3-3"/>
    </svg>
  );
}
export function ArrowRightIcon() {
  return (
    <svg className="lg-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  );
}
export function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  );
}

// ── Cursor-follow glow / spotlight ────────────────────────────────────
export function trackPointer(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width * 100).toFixed(1)}%`);
  e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height * 100).toFixed(1)}%`);
}

// ── Floating-label field ──────────────────────────────────────────────
export function Field({ name, type, label, icon, value, onChange, error, readOnly, trailing, autoComplete: ac }) {
  return (
    <div className="lg-field">
      <div className={`lg-control${error ? ' lg-control--invalid' : ''}`}>
        <span className="lg-field-ico">{icon}</span>
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder=" "
          readOnly={readOnly}
          autoComplete={ac || 'off'}
          aria-invalid={error ? 'true' : undefined}
        />
        <label htmlFor={name}>{label}</label>
        {trailing && <span className="lg-field-end">{trailing}</span>}
      </div>
      {error && (
        <p className="lg-field-error" role="alert">
          <AlertCircleIcon/> {error}
        </p>
      )}
    </div>
  );
}

// ── Left-stage data ───────────────────────────────────────────────────
const MODULES = [
  { title: 'Third-Party Risk',      code: 'TPCRM', tag: 'Vendors that respond',          icon: 'tpcrm'      },
  { title: 'External Exposure',     code: 'EASM',  tag: 'See what attackers see',         icon: 'easm'       },
  { title: 'Continuous Compliance', code: 'GRC',   tag: 'Weeks, not months',              icon: 'compliance' },
  { title: 'AI Governance',         code: 'AI',    tag: 'Adopt AI safely',                icon: 'nova'       },
];

function Clouds() {
  return (
    <div className="lg-clouds" aria-hidden="true">
      <div className="lg-cloud lg-cloud--far">
        <svg width="2000" height="1080" viewBox="0 0 2000 1080">
          <filter id="lg-cf" filterUnits="userSpaceOnUse" x="0" y="0" width="2000" height="1080" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.0016 0.0062" numOctaves={5} seed={4} stitchTiles="stitch"/>
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  3.4 0 0 0 -1.45"/>
          </filter>
          <rect width="2000" height="1080" filter="url(#lg-cf)"/>
        </svg>
      </div>
      <div className="lg-cloud lg-cloud--near">
        <svg width="2000" height="1080" viewBox="0 0 2000 1080">
          <filter id="lg-cn" filterUnits="userSpaceOnUse" x="0" y="0" width="2000" height="1080" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.0024 0.0078" numOctaves={6} seed={11} stitchTiles="stitch"/>
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  3.8 0 0 0 -1.62"/>
            <feGaussianBlur stdDeviation="1.2"/>
          </filter>
          <rect width="2000" height="1080" filter="url(#lg-cn)"/>
        </svg>
      </div>
    </div>
  );
}

// ── Left section: sky stage with branding and module cards ────────────
function AuthStage({ eyebrow, headline, accent, modules }) {
  const _eyebrow  = eyebrow  ?? 'Cyber risk intelligence platform';
  const _headline = headline ?? 'See the risk';
  const _accent   = accent   ?? 'before it costs you.';
  const _modules  = modules  ?? MODULES;

  return (
    <aside className="lg-stage">
      <Clouds/>
      <div className="lg-stage-inner">
        <div className="lg-logo lg-rise" style={{'--i': 0}}>
          <img src={foreseeLogo} alt="Foresee Security"/>
          <div className="lg-brand-text">
            <span className="lg-brand-name">FORESEE</span>
            <span className="lg-brand-sub">SECURITY</span>
          </div>
        </div>
        <div className="lg-hero">
          <p className="lg-eyebrow-hero lg-rise" style={{'--i': 2}}>
            {_eyebrow}
          </p>
          <h2 className="lg-headline lg-rise" style={{'--i': 3}}>
            {_headline}{' '}
            <span className="lg-accent">{_accent}</span>
          </h2>
          <ul className="lg-modules">
            {_modules.map(({title, code, tag, icon}, i) => (
              <li className="lg-rise" style={{'--i': 3 + i}} key={code}>
                <div className="lg-module-top">
                  <span className="lg-module-ico">
                    <ModuleIcon name={icon} size={20} color="currentColor"/>
                  </span>
                  <span className="lg-pill">{tag}</span>
                </div>
                <b>{title}</b>
                <small>{code}</small>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

// ── Right section: white form panel ──────────────────────────────────
function AuthPanel({ children }) {
  return (
    <section className="lg-panel" onPointerMove={trackPointer}>
      {children}
    </section>
  );
}

// ── Layout wrapper: two-column grid ──────────────────────────────────
function AuthLayout({ children }) {
  return <main className="lg">{children}</main>;
}

AuthLayout.Stage = AuthStage;
AuthLayout.Panel = AuthPanel;

export default AuthLayout;
